import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Alert } from "@/components/alert";
import { Button } from "@/components/button";

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** Question form. e.g. "Disconnect SSO?" */
  title: string;
  /** One-line explanation of the action's consequence. */
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Renders the confirm button with destructive styling. */
  destructive?: boolean;
  /** In-flight: disables both buttons + blocks dismissal until the action settles. */
  busy?: boolean;
  /** Confirm-button label while `busy` (e.g. "Deleting…"). Falls back to confirmLabel. */
  busyLabel?: string;
  /** Inline error surfaced under the description after a failed confirm (role=alert). */
  error?: string;
  /**
   * Disables just the confirm button (cancel stays enabled) — for a dialog
   * shown to explain WHY an action isn't allowed right now (e.g. disconnecting
   * your last SSO provider while your company enforces SSO), rather than to
   * confirm it.
   */
  confirmDisabled?: boolean;
  insetLeft?: number;
};

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  busy = false,
  busyLabel,
  error,
  confirmDisabled = false,
  insetLeft = 0,
}: Props) {
  // Don't dismiss while the confirmed action is in flight (backdrop / Escape).
  const requestClose = () => {
    if (!busy) onClose();
  };

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") requestClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onClose]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    // The backdrop dims the full viewport (sidebar included); the inner
    // container is inset by `insetLeft` so the panel centers within the
    // visible content area instead of the whole screen — mirrors Modal.
    <div className="fixed inset-0 z-[110] bg-grey-900-24" onClick={requestClose}>
      <div
        style={{ left: insetLeft }}
        className="absolute inset-y-0 right-0 flex items-center justify-center p-4"
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-label={title}
          onClick={(e) => e.stopPropagation()}
          style={{ width: 480 }}
          className="flex max-h-[calc(100vh-2rem)] max-w-full flex-col gap-8 overflow-y-auto rounded-[4px] border border-grey-200 bg-white p-6 shadow-soft"
        >
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-1">
              <h2 className="text-h4 text-black">{title}</h2>
              {description && <p className="text-small text-grey-900">{description}</p>}
            </div>
            {error && (
              <Alert tone="danger" role="alert">
                {error}
              </Alert>
            )}
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button hierarchy="secondary" size="md" onClick={onClose} disabled={busy}>
              {cancelLabel}
            </Button>
            <Button
              hierarchy={destructive ? "destructive" : "primary"}
              size="md"
              onClick={onConfirm}
              disabled={busy || confirmDisabled}
            >
              {busy ? (busyLabel ?? confirmLabel) : confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
